import { api } from "@/lib/api";

export type RecordData = Record<string, unknown>;
export interface Provenance extends RecordData { environment?: string; as_of?: string; source?: string }
export interface PageData { items: RecordData[]; total: number; page: number; page_size: number; provenance?: Provenance }
export interface CatalogObject extends RecordData { path: string; kind: string; metadata?: RecordData; spec?: RecordData }
export interface Catalog extends RecordData { objects: CatalogObject[]; source_commit?: string; contract_commit?: string; source_hash?: string; bundle_id?: string }
export interface Bundle extends RecordData { id: string; version: string; state: string; row_version: number; payload?: { files?: Record<string, string> }; validation_report?: RecordData }
export interface Capabilities extends RecordData { can_activate: false; can_read_business?: boolean; can_write_config?: boolean; can_seal?: boolean; can_read_prompts?: boolean; can_export?: boolean; environment?: string }
export const CRYPTO_API = "/quantum/business/crypto";

export function object(value: unknown): RecordData { return value && typeof value === "object" && !Array.isArray(value) ? value as RecordData : {}; }
export function list(value: unknown): RecordData[] { return Array.isArray(value) ? value.filter(v => v && typeof v === "object") as RecordData[] : []; }
export function str(value: unknown): string { return value === null || value === undefined ? "" : String(value); }
export function pick(row: RecordData, ...keys: string[]): unknown { for (const key of keys) if (row[key] !== undefined && row[key] !== null) return row[key]; return null; }

export function apiError(error: unknown): string {
  const response = object(object(error).response);
  if (response.status === 403) return "当前账号没有访问此内容的权限。";
  if (response.status === 409) return "版本已被其他管理员修改。请重新加载并比较内容后保存；本次修改未覆盖服务器版本。";
  const body = object(response.data);
  const detail = object(body.detail);
  return str(pick(object(body.error), "message") || pick(detail, "message", "code") || pick(body, "message", "detail") || object(error).message) || "请求失败，请稍后重试。";
}

async function unwrap<T>(promise: Promise<{ data: { code?: number | string; message?: string; error?: { message?: string }; data: T } }>): Promise<T> {
  const response = await promise;
  const envelope = response.data;
  if (envelope?.code !== undefined && envelope.code !== 0 && envelope.code !== "0") throw new Error(envelope.message || envelope.error?.message || "请求未完成");
  return envelope?.data as T;
}

export const cryptoApi = {
  get: <T = RecordData>(path: string, params?: RecordData, signal?: AbortSignal) => unwrap<T>(api.get(`${CRYPTO_API}${path}`, { params, signal })),
  post: <T = RecordData>(path: string, data: unknown) => unwrap<T>(api.post(`${CRYPTO_API}${path}`, data)),
  put: <T = RecordData>(path: string, data: unknown) => unwrap<T>(api.put(`${CRYPTO_API}${path}`, data)),
  async exportBundle(id: string): Promise<void> {
    const response = await api.get(`${CRYPTO_API}/config/bundles/${encodeURIComponent(id)}/export`, { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `crypto-registry-${id}.zip`;
    anchor.click();
    URL.revokeObjectURL(url);
  },
};

export function configName(item: CatalogObject): string { return str(pick(object(item.spec), "display_name", "name", "label") || pick(object(item.metadata), "name", "id") || item.id || item.path); }
export function configIdentity(item: CatalogObject): string { return str(pick(object(item.metadata), "id", "name") || item.id || item.path); }
export function configLink(kind: string, path: string, bundle?: string): string { return `/crypto/${kind}/${encodeURIComponent(path)}${bundle ? `?bundle=${encodeURIComponent(bundle)}` : ""}`; }
export function preserveFiles(files: Record<string, string>, path: string, value: string): Record<string, string> {
  if (!(path in files)) throw new Error("文件不属于当前配置包，不能添加未知路径。");
  return { ...files, [path]: value };
}

export function matchesReference(item: CatalogObject, reference: string): boolean {
  return reference === configIdentity(item) || reference === str(item.ref) || reference === `${configIdentity(item)}@${str(item.version || object(item.metadata).version)}`;
}
export function strategyMatrix(objects: CatalogObject[]) {
  const cells: { symbol: string; market: string; profile: CatalogObject; parameters: CatalogObject }[] = [];
  for (const parameters of objects.filter(o => o.kind.includes("StrategySymbolParameters"))) {
    const data = object(parameters.spec);
    const profile = objects.find(o => o.kind.includes("StrategyProfile") && o.registry_path === parameters.registry_path && matchesReference(o, str(data.strategy_profile_ref)));
    if (profile) cells.push({ symbol: str(data.symbol), market: str(data.market_type), profile, parameters });
  }
  return cells;
}

export function creationAvailability(item: CatalogObject, publicConfig: unknown): string {
  if (item.source_state === "retained" || item.path.startsWith("strategy-archives/")) return "历史保留";
  const createAgent = object(object(publicConfig).create_agent);
  if (!Array.isArray(createAgent.strategy_profiles)) return "创建可用性未核验";
  return list(createAgent.strategy_profiles).some(p => p.profile_id === configIdentity(item)) ? "可新建 Agent" : "仅登记 / 不可新建";
}

/** Follow declared configuration references only. Release membership is not a dependency. */
export function relatedConfiguration(item: CatalogObject, objects: CatalogObject[], type: string): CatalogObject[] {
  const scoped = objects.filter(o => o.registry_path === item.registry_path);
  const data = object(item.spec);
  if (type === "strategy") return scoped.filter(o =>
    (o.kind.includes("StrategySpec") && matchesReference(o, str(data.strategy_spec_id))) ||
    (o.kind.includes("StrategySymbolParameters") && matchesReference(item, str(object(o.spec).strategy_profile_ref))),
  );
  const keys = type === "prompts"
    ? ["prompt_artifact_id", "prompt_strategy_policy_id", "default_decision_style", "allowed_decision_styles", "default_decision_cycle_id", "allowed_decision_cycle_ids"]
    : ["strategy_spec_id", "research_depth_policy_id", "runtime_policy_id", "evidence_policy_id"];
  const refs = keys.flatMap(key => Array.isArray(data[key]) ? data[key] as string[] : typeof data[key] === "string" ? [data[key] as string] : []);
  const direct = scoped.filter(o => refs.some(ref => matchesReference(o, ref)));
  if (type !== "prompts") return direct;
  // Prompt Part data.value.part_id is a registered alias used by style definitions.
  const partIds = direct.flatMap(o => [object(o.spec).prompt_part_id, object(o.spec).prompt_part_ref]).filter(Boolean).map(str);
  const parts = scoped.filter(o => o.kind.includes("PromptPart") && partIds.some(ref => matchesReference(o, ref) || object(object(o.spec).value).part_id === ref));
  return [...new Set([...direct, ...parts])];
}

export const businessTitles: Record<string, string> = { users: "用户列表", accounts: "交易账户", agents: "Trade Agent", decisions: "AI 决策记录", orders: "订单意图", fills: "成交记录", positions: "持仓记录", plans: "待执行计划", "model-usage": "模型调用统计", "runtime-releases": "C 端运行版本" };

export function runtimeReleaseStatus(id: unknown, heads: RecordData[] | null): string {
  if (!heads?.length) return "运行版本头未记录";
  const active = heads.some(h => str(h.active_release_id) === str(id));
  const staged = heads.some(h => str(h.staged_release_id) === str(id));
  return active && staged ? "当前生效 / 已暂存" : active ? "当前生效" : staged ? "已暂存 / 未生效" : "历史版本";
}
